import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { barIcon, barLineIcon, commonBarTHIcon, lineIcon, stackLineIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import SteamScoreTrendBox from '@op/pages/gamestore/widgets/ScoreTrendsPcConsole/SteamScoreTrendBox';
import ReviewsRealtime from '@op/pages/SteamPerformance/components/ReviewsRealtime';
import SteamStackedTimeline from '@op/pages/SteamPerformance/components/SteamStackedTimeline';
import { keyValueList, TabType } from '@op/pages/SteamPerformance/RealtimeRatings/RealtimeFilteredReviews';
import { Segment } from 'tea-component';
import { dealwithFilteredReviewsTableData, dealwithSelectedTimeline } from '@op/pages/SteamPerformance/utils';
import { downloadAction } from '@op/components/DownloadCommon/download-page';
import { downloadAction as downloadActionNew } from '@op/pages/download/utils';
import { IRealtimeSteamHourlyAddedReviews } from '../../type';
import { ReviewsTable } from '@op/pages/SteamPerformance/components/ReviewsTable';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function SteamHourlyAddedReviews(props: IRealtimeSteamHourlyAddedReviews) {
  const { filter, timelineData, timelineLoading, tab: propsTab = 'steam-all', gameInfo, height = 346, tableHeight = 314.5 } = props;
  const { formatMessage } = useLanguage();
  const [tab, setTab] = useState<TabType>('all');
  const [chartType, setChartType] = useState('bar-line');

  const { oldValue, newValue, platformData, isEmpty } = useMemo(() => {
    const { timeline_of_start_time, timeline_of_end_time, list, old_list } = timelineData || {};

    const platformData = {
      timeline_of_start_time,
      timeline_of_end_time,
      list,
    };
    if (tab === 'all') {
      const { select_recommend } = list || {};
      return {
        ...dealwithSelectedTimeline(list, old_list),
        platformData,
        isEmpty: !(select_recommend ?? []).some((val) => val.review)
      };
    }

    const { select_recommend, select_non_recommend } = list;
    const {
      select_recommend: oldSelectRecommend,
      select_non_recommend: oldNonSelectRecommend,
    } = old_list;

    if (tab === 'positive') {
      return {
        oldValue: oldSelectRecommend,
        newValue: select_recommend,
        platformData,
        isEmpty: !(select_recommend ?? []).some((val) => val.review)
      };
    }

    return {
      oldValue: oldNonSelectRecommend,
      newValue: select_non_recommend,
      platformData,
      isEmpty: !(select_non_recommend ?? []).some((val) => val.review)
    };
  }, [timelineData, tab]);

  const chartIconList = useMemo(() => {
    const list = propsTab === 'steam-all' ? [barIcon, stackLineIcon, lineIcon] : [tableIcon];
    if (tab === 'all') {
      list.unshift(barLineIcon, commonBarTHIcon);
    }

    return list;
  }, [tab, propsTab]);

  function downloadReviewsInfo() {
    if (propsTab === 'steam-all') {
      const name =
        chartType !== 'stack_line'
          ? 'Realtime Added Reviews'
          : 'Realtime Added Stacked Reviews';
      downloadAction({
        limit: 150000,
        downLoadParams: {
          ...filter,
          need_link: true,
          channel: ['steam'],
          channels: ['steam'],
          metrics: ['recommend', 'non_recommend'],
          ret_metrics: ['recommend', 'non_recommend'],
          types: ['total', 'slice'],
        },
        downloadFunc: 'getOverviewPcConsoleTimeline',
        simpleName: name,
        sheetName: name,
      });
    } else {
      downloadActionNew({
        key: 'RealtimeOverviewMetricsDownload',
        fileName: `Realtime Databrain All Reviews`,
        filter: {
          ...filter,
          ...gameInfo,
        },
        auxiliaryParams: {
          columns: [
            { name: 'Positive', value: 'select_recommend' },
            { name: 'Negative', value: 'select_non_recommend' },
            { name: 'Positive%', value: 'select_recommend_score' },
          ]
        },
        data: tableData
      });
    }
  }

  const tableData = useMemo(() => {
    const { list } = timelineData || {};
    return dealwithFilteredReviewsTableData(list);
  }, [timelineData]);

  return (
    <CardCommon
      contentCard
      // title={
      //   <>
      //     {
      //       propsTab === 'steam-all' && (
      //         <Segment
      //           className="filter-reviews-segment"
      //           value={tab}
      //           onChange={(value: any) => {
      //             setTab(value);
      //             if (
      //               ['negative', 'positive'].includes(value) &&
      //               ['bar-line', 'common-th-bar'].includes(chartType)
      //             ) {
      //               setChartType('bar');
      //             }
      //             buriedBtnPoint('RO_Steam_AddedReviews_Tab', { value, type: propsTab });
      //           }}
      //           options={[
      //             { text: formatMessage({ id: 'All' }), value: 'all' },
      //             {
      //               text: (
      //                 <>
      //                   <i className="icon-positive"></i>
      //                   {formatMessage({ id: 'Positive' })}
      //                 </>
      //               ),
      //               value: 'positive',
      //             },
      //             {
      //               text: (
      //                 <>
      //                   <i className="icon-negative"></i>
      //                   {formatMessage({ id: 'Negative' })}
      //                 </>
      //               ),
      //               value: 'negative',
      //             },
      //           ]}
      //         />
      //       )
      //     }
      //   </>
      // }
      loading={timelineLoading}
      operations={chartIconList}
      operationInfo={[chartType, setChartType]}
      hasDownload={!isEmpty}
      handleDownloadOnClick={() => downloadReviewsInfo()}
      className={'dbo-realtime-steam-added-reviews'}
      loadingHeight={height}
      logBtnId='RO_Steam_Databrain'
      extraInfo={{ type: propsTab, tab }}
    >
      <>
        {
          ['bar-line', 'common-th-bar'].includes(chartType) && (
            <SteamScoreTrendBox
              platformData={platformData}
              filterParams={filter}
              height={height}
              negativeLessThanZero={chartType === 'common-th-bar'}
            />
          )
        }
        {
          ['line', 'bar'].includes(chartType) && (
            <ReviewsRealtime
              oldValue={oldValue}
              newValue={newValue}
              filter={filter}
              top={30}
              height={height}
              calculationMaxMin
              isSelected
              chartType={chartType}
              leftTitle={formatMessage({ id: 'Mentions' })}
              valueKey="review"
              showEndLabel
              {...keyValueList[tab]}
            />
          )
        }
        {
          ['stack_line'].includes(chartType) && (
            <SteamStackedTimeline
              oldValue={oldValue}
              leftTitle={formatMessage({ id: 'Mentions' })}
              newValue={newValue}
              height={height}
              filter={filter}
              {...keyValueList[tab]}
            />
          )
        }
        {
          ['table'].includes(chartType) && (
            <ReviewsTable
              filter={filter}
              recordKey="time"
              records={tableData}
              height={tableHeight}
              columns={[
                { text: 'Date', value: 'time', isDate: true, align: 'left' },
                { text: 'Positive', value: 'select_recommend' },
                { text: 'Negative', value: 'select_non_recommend' },
                {
                  text: 'Positive%',
                  value: 'select_recommend_score',
                  isScore: true,
                },
              ]}
            ></ReviewsTable>
          )
        }
      </>
    </CardCommon>
  );
}